/**
 * 2021-2024 Stadt Wolfsburg
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import {connect} from 'react-redux';

import axios from 'axios';
import isEmpty from 'lodash.isempty';
import assign from 'object-assign';
import PropTypes from 'prop-types';
import {LayerRole} from 'qwc2/actions/layers';
import {setCurrentTask} from 'qwc2/actions/task';
import ResizeableWindow from 'qwc2/components/ResizeableWindow';
import ConfigUtils from 'qwc2/utils/ConfigUtils';
import LayerUtils from 'qwc2/utils/LayerUtils';
import LocaleUtils from 'qwc2/utils/LocaleUtils';
import MapUtils from 'qwc2/utils/MapUtils';
import ReactDOMServer from 'react-dom/server';
import {v4 as uuidv4} from 'uuid';

import './style/Legend.css';

class WobLegend extends React.Component {
    static propTypes = {
        bboxDependentLegend: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        configPath: PropTypes.string,
        currentTask: PropTypes.string,
        defaultConfig: PropTypes.object,
        enableLegendPrint: PropTypes.bool,
        initiallyDocked: PropTypes.bool,
        layers: PropTypes.array,
        map: PropTypes.object,
        scaleDependentLegend: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        setCurrentTask: PropTypes.func,
        templatePath: PropTypes.string,
        windowSize: PropTypes.object
    };
    static defaultProps = {
        bboxDependentLegend: false,
        configPath: "",
        defaultConfig: {
            defaultIgnoreLayers: [],
            defaultBboxDependentLegend: false,
            defaultScaleDependentLegend: false,
            legends: {}
        },
        enableLegendPrint: false,
        initiallyDocked: false,
        layers: [],
        scaleDependentLegend: false,
        templatePath: ":/templates/legendprint.html",
        windowSize: {width: 320, height: 480}
    };
    state = {
        active: false,
        config: null
    };
    constructor(props) {
        super(props);
        this.legendPrintWindow = null;
        window.addEventListener('beforeunload', () => {
            if (this.legendPrintWindow && !this.legendPrintWindow.closed) {
                this.legendPrintWindow.close();
            }
        });
    }
    componentDidMount() {
        // Add assets path to configuration file path
        let url = this.props.configPath;
        if (url && url.startsWith(":/")) {
            url = ConfigUtils.getAssetsPath() + this.props.configPath.substr(1);
        }

        // Load and parse configuration file
        axios.get(url).then(response => {
            this.parseConfig(response.data);
        }).catch(() => {
            // eslint-disable-next-line no-console
            console.warn("Error on loading legend configuration file");
        });
    }
    componentDidUpdate(prevProps) {
        // If "WobLegend" is the current task, then open the legend window, if not already opened
        if (this.props.currentTask === 'WobLegend' && prevProps.currentTask !== 'WobLegend') {
            this.activate();
        }
    }
    render() {
        // Cancel if legend window is not activated or no configuration exists
        if (!this.state.active || !this.state.config) {
            return null;
        }

        // Extra controls for the resizeable legend window
        const extraControls = [];
        if (this.props.enableLegendPrint) {
            extraControls.push({icon: "print", callback: this.printLegend});
        }

        // Render resizeable legend window
        return (
            <ResizeableWindow extraControls={extraControls}
                icon="info-sign"
                initialHeight={this.props.windowSize.height}
                initialWidth={this.props.windowSize.width}
                initialX={0}
                initialY={0}
                initiallyDocked={this.props.initiallyDocked}
                onClose={this.deactivate}
                scrollable
                title={LocaleUtils.tr("wob.legend.title")}
                zIndex={9}>
                {this.renderBody()}
            </ResizeableWindow>
        );
    }
    renderBody = () => {
        // Legend items
        const legendItems = [];
        for (const layer of this.props.layers) {
            // Create legend for the currrent layer
            const layerLegend = this.renderLayer(layer);

            if (layerLegend) {
                legendItems.push(layerLegend);
            }
        }

        // Legend window content
        let content = null;

        // Render legend items if one or more exist
        if (legendItems.length > 0) {
            content = legendItems;
        // Show message for empty legend if no item exist
        } else {
            content = (<div className="wob-legend-empty-message">{LocaleUtils.tr("wob.legend.noLegend")}</div>);
        }

        // Return legend body result
        return (
            <div className="wob-legend-window-body" role="body">
                {content}
            </div>
        );
    };
    renderLayer = (layer) => {
        // Skip layer that is not a theme or a user layer
        if (layer.role !== LayerRole.THEME && layer.role !== LayerRole.USERLAYER) {
            return null;
        // Skip not visible layer
        } else if (!layer.visibility) {
            return null;
        } else {
            // Title of the legend section for the current layer
            const title = layer.title;

            // Create legends for the sublayers of the current layer
            let sublayerLegends = [];

            // If a configuration exists, then create legends for the layer and its sublayers as specified in the configuration
            if (this.state.config) {
                sublayerLegends = this.renderLegendConfig(layer, this.state.config);
            // Otherwise create legends for the sublayers of the current layer in the default settings
            } else {
                for (const sublayer of LayerUtils.explodeLayers([layer])) {
                    // If the group isn't visible, don't show the legend
                    if (!sublayer.visibility) {
                        const sublayerLegend = this.renderSublayer(layer, sublayer.sublayer, this.props.bboxDependentLegend, this.props.scaleDependentLegend);

                        if (sublayerLegend) {
                            sublayerLegends.push(sublayerLegend);
                        }
                    }
                }
            }

            // Return rendered layer legend if one or more rendered sublayer legends exist
            if (sublayerLegends.length > 0) {
                return (
                    <div key={layer.uuid}>
                        <h4 className="wob-legend-window-title">{title}</h4>
                        <div className="wob-legend-window-frame">
                            {sublayerLegends}
                        </div>
                    </div>
                );
            } else {
                return null;
            }
        }
    };
    renderSublayer = (layer, sublayer, bboxDependentLegend, scaleDependentLegend) => {
        // Get sublayer path
        const sublayerpath = [];
        const searchResult = LayerUtils.searchSubLayer(layer, "uuid", sublayer.uuid, sublayerpath);
        if (!searchResult) {
            return null;
        }

        // Skip not visible sublayer
        if (!LayerUtils.sublayerVisible(layer, sublayerpath)) {
            return null;
        } else {
            // Rendered sublayer legend result
            let legend = null;

            // Create image tag when a legend url is available
            if (layer.legendUrl) {
                const scale = MapUtils.computeForZoom(this.props.map.scales, this.props.map.zoom);

                // Create URL for the legend image
                const legendUrl = LayerUtils.getLegendUrl(layer, sublayer, scale, this.props.map, bboxDependentLegend, scaleDependentLegend);

                legend = (<img className="wob-legend-window-legend" src={legendUrl} />);
            // Create span tag when a color is present
            } else if (layer.color) {
                legend = (<span className="wob-legend-window-coloricon" style={{backgroundColor: layer.color}} />);
            }

            // Return rendered sublayer legend result
            return legend ? (<div key={sublayer.uuid}>{legend}</div>) : null;
        }
    };
    renderText = (text) => {
        let html = text;

        // Add asset path to eventually existing source paths
        const srcRegex = /(src=["'])(:\/)/gmi;
        html = html.replace(srcRegex, "$1" + ConfigUtils.getAssetsPath() + "/");

        // Return rendered text as html
        return (
            <div key={uuidv4()}>
                <span className="wob-legend-window-text" dangerouslySetInnerHTML={{__html: html}} />
            </div>
        );
    };
    renderImage = (url, width = null, height = null) => {
        // Add assets path to image path
        let imageUrl = url;
        if (imageUrl && imageUrl.startsWith(":/")) {
            imageUrl = ConfigUtils.getAssetsPath() + imageUrl.substr(1);
        }

        // Image size
        const imageWidth = width ? width : "auto";
        const imageHeight = height ? height : "auto";

        // Return rendered image
        return (
            <div key={uuidv4()}>
                <img className="wob-legend-window-image" src={imageUrl} style={{width: imageWidth, height: imageHeight}} />
            </div>
        );
    };
    renderLegendConfig = (layer, config) => {
        // Results of the rendered items
        const renderedItems = [];

        // Cancel if no layer or configuration exists
        if (layer === undefined || config === undefined) {
            return renderedItems;
        }

        // Get configuration section "legends" for the current layer
        let layerConfig = null;
        if (config.legends !== undefined && layer.name !== undefined && config.legends[layer.name]) {
            layerConfig = config.legends[layer.name];
        }

        // Layers to be skipped
        let ignoreLayers = [];

        // Get layers to be skipped by default configuration
        if (Array.isArray(config.defaultIgnoreLayers)) {
            ignoreLayers = config.defaultIgnoreLayers;
        }

        // Get layers to be skipped by layer configuration
        if (layerConfig && Array.isArray(layerConfig.ignoreLayers)) {
            ignoreLayers = layerConfig.ignoreLayers;
        }

        // Bbox dependent visibility of layers when creating legend images
        let bboxDependentLegend = false;

        // Get bbox dependent visibility parameter by default configuration
        if (typeof config.defaultBboxDependentLegend === "boolean" || typeof config.defaultBboxDependentLegend === "string") {
            bboxDependentLegend = config.defaultBboxDependentLegend;
        }

        // Get bbox dependent visibility parameter by layer configuration
        if (layerConfig && typeof layerConfig.bboxDependentLegend === "boolean" || typeof config.bboxDependentLegend === "string") {
            bboxDependentLegend = layerConfig.bboxDependentLegend;
        }

        // Scale dependent visibility of layers when creating legend images
        let scaleDependentLegend = false;

        // Get scale dependent visibility parameter by default configuration
        if (typeof config.defaultScaleDependentLegend === "boolean" || typeof config.defaultScaleDependentLegend === "string") {
            scaleDependentLegend = config.defaultScaleDependentLegend;
        }

        // Get scale dependent visibility parameter by layer configuration
        if (layerConfig && typeof layerConfig.scaleDependentLegend === "boolean" || typeof config.scaleDependentLegend === "string") {
            scaleDependentLegend = layerConfig.scaleDependentLegend;
        }

        // If legend items exist in configuration
        if (layerConfig && layerConfig.items !== undefined) {
            // For each legend item of the configuration
            for (const item of layerConfig.items) {
                // Get item parameters
                const layerParam = typeof item.layer === "string" ? item.layer : "";
                const showForLayersParam = Array.isArray(item.showForLayers) ? item.showForLayers : [];
                const hideForLayersParam = Array.isArray(item.hideForLayers) ? item.hideForLayers : [];
                const bboxDependentLegendParam = typeof item.bboxDependentLegend === "boolean" || typeof item.bboxDependentLegend === "string" ? item.bboxDependentLegend : bboxDependentLegend;
                const scaleDependentLegendParam = typeof item.scaleDependentLegend === "boolean" || typeof item.scaleDependentLegend === "string" ? item.scaleDependentLegend : scaleDependentLegend;
                const textParam = typeof item.text === "string" ? item.text : "";
                const imageUrlParam = typeof item.imageUrl === "string" ? item.imageUrl : "";
                const imageWidthParam = typeof item.imageWidth === "string" ? item.imageWidth : "";
                const imageHeightParam = typeof item.imageHeight === "string" ? item.imageHeight : "";

                // Skip item if necessary layers are specified and they are not available
                if (showForLayersParam && showForLayersParam.length > 0) {
                    let skipCurrentItem = true;
                    for (const sublayerName of showForLayersParam) {
                        const sublayer = LayerUtils.searchSubLayer(layer, "name", sublayerName);
                        if (sublayer) {
                            const sublayerVisibility = isEmpty(sublayer.sublayers) ? sublayer.visibility : this.getGroupVisibility(sublayer);
                            if (sublayerVisibility) {
                                skipCurrentItem = false;
                                break;
                            }
                        }
                    }
                    if (skipCurrentItem) {
                        continue;
                    }
                }

                // Skip item if unnecessary layers are specified and they are available
                if (hideForLayersParam && hideForLayersParam.length > 0) {
                    let skipCurrentItem = false;
                    for (const sublayerName of hideForLayersParam) {
                        const sublayer = LayerUtils.searchSubLayer(layer, "name", sublayerName);
                        if (sublayer) {
                            const sublayerVisibility = isEmpty(sublayer.sublayers) ? sublayer.visibility : this.getGroupVisibility(sublayer);
                            if (sublayerVisibility) {
                                skipCurrentItem = true;
                                break;
                            }
                        }
                    }
                    if (skipCurrentItem) {
                        continue;
                    }
                }

                // If a layer name is specified and the layer should not be ignored
                if (layerParam && ignoreLayers.indexOf(layerParam) === -1) {
                    // Search sublayer by name
                    const sublayer = LayerUtils.searchSubLayer(layer, "name", layerParam);

                    // If a sublayer was found and the sublayer is visible
                    if (sublayer && sublayer.visibility) {
                        // Render text if specified
                        if (textParam) {
                            const renderedText = this.renderText(textParam);
                            renderedItems.push(renderedText);
                        }

                        // Render image if specified
                        if (imageUrlParam) {
                            const renderedImage = this.renderImage(imageUrlParam, imageWidthParam, imageHeightParam);
                            renderedItems.push(renderedImage);
                        }

                        // If no text or image is specified, then render the legend image of the sublayer
                        if (!textParam && !imageUrlParam) {
                            const renderedSublayer = this.renderSublayer(layer, sublayer, bboxDependentLegendParam, scaleDependentLegendParam);
                            if (renderedSublayer) {
                                renderedItems.push(renderedSublayer);
                            }
                        }
                    }
                // If no layer name is specified
                } else {
                    // Render text if specified
                    if (textParam) {
                        const renderedText = this.renderText(textParam);
                        renderedItems.push(renderedText);
                    }

                    // Render image if specified
                    if (imageUrlParam) {
                        const renderedImage = this.renderImage(imageUrlParam, imageWidthParam, imageHeightParam);
                        renderedItems.push(renderedImage);
                    }
                }
            }
        // If no legend items exist in configuration, then render the sublayers by default settings
        } else {
            for (const sublayer of LayerUtils.explodeLayers([layer])) {
                if (sublayer.sublayer.name && ignoreLayers.indexOf(sublayer.sublayer.name) === -1) {
                    const renderedSublayer = this.renderSublayer(layer, sublayer.sublayer, bboxDependentLegend, scaleDependentLegend);

                    if (renderedSublayer) {
                        renderedItems.push(renderedSublayer);
                    }
                }
            }
        }
        return renderedItems;
    };
    activate = () => {
        // Set legend window state to active
        this.setState({active: true});
    };
    deactivate = () => {
        // Set legend window state to inactive
        this.setState({active: false});
    };
    parseConfig = (data) => {
        // Parse configuration file
        let config;
        if (typeof data === 'object') {
            config = assign({}, this.props.defaultConfig, data);
        } else {
            config = assign({}, this.props.defaultConfig);
            // eslint-disable-next-line no-console
            console.warn('Broken legend configuration file "' + this.props.configPath + '"!');
        }
        this.setState({config: config});
    };
    getGroupVisibility = (group) => {
        if (isEmpty(group.sublayers) || group.visibility === false) {
            return 0;
        }
        let visible = 0;
        group.sublayers.map(sublayer => {
            const sublayervisibility = sublayer.visibility === undefined ? true : sublayer.visibility;
            if (sublayer.sublayers && sublayervisibility) {
                visible += this.getGroupVisibility(sublayer);
            } else {
                visible += sublayervisibility ? 1 : 0;
            }
        });
        return visible / group.sublayers.length;
    };
    printLegend = () => {
        let body = '<div id="legendcontainerbody">';
        const printLabel = LocaleUtils.tr("wob.legend.printlegend");
        body += '<div id="print">' +
                '<style type="text/css">@media print{ #print { display: none; }}</style>' +
                '<button onClick="(function(){window.print();})()">' + printLabel + '</button>' +
                '</div>';
        body += ReactDOMServer.renderToString(this.renderBody());
        body += "</div>";
        const setLegendPrintContent = () => {
            const container = this.legendPrintWindow.document.getElementById("legendcontainer");
            if (container) {
                container.innerHTML = body;
            } else {
                this.legendPrintWindow.document.body.innerHTML = "Broken template. An element with id=legendcontainer must exist.";
            }
        };

        if (this.legendPrintWindow && !this.legendPrintWindow.closed) {
            setLegendPrintContent();
            this.legendPrintWindow.focus();
        } else {
            let templatePath = this.props.templatePath;
            if (templatePath.startsWith(":/")) {
                const assetsPath = ConfigUtils.getAssetsPath();
                templatePath = assetsPath + templatePath.substr(1);
            }
            this.legendPrintWindow = window.open(templatePath, "Legend", "toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes");
            if (window.navigator.userAgent.indexOf('Trident/') > 0) {
                // IE...
                const interval = setInterval(() => {
                    if (this.legendPrintWindow.document.readyState === 'complete') {
                        setLegendPrintContent();
                        clearInterval(interval);
                    }
                });
            } else {
                this.legendPrintWindow.addEventListener('load', setLegendPrintContent, false);
            }
        }
    };
}

const selector = (state) => ({
    currentTask: state.task.id,
    layers: state.layers.flat,
    map: state.map
});

export default connect(selector, {
    setCurrentTask: setCurrentTask
})(WobLegend);
